export const PROMOTION_TYPES = {
  SPECIAL_PRICE: "PRECIO_ESPECIAL",
  QUANTITY_BONUS: "CANTIDAD_BONIFICADA",
  BUNDLE: "PAQUETE",
};

export const createPromotionTarget = (target = {}) => ({
  targetUnitMeasureId:
    target.targetUnitMeasureId === undefined
      ? null
      : target.targetUnitMeasureId,
  targetUnitMeasureName: target.targetUnitMeasureName || "",
  targetUnitLabel: target.targetUnitLabel || "",
  targetQuantityContained: Number(target.targetQuantityContained || 1),
});

export const getCurrentLocalDateTimeValue = () => {
  const currentDate = new Date();
  const timezoneOffset = currentDate.getTimezoneOffset() * 60000;
  return new Date(currentDate.getTime() - timezoneOffset)
    .toISOString()
    .slice(0, 16);
};

export const getPromotionTypeDefaults = (type) => {
  if (type === PROMOTION_TYPES.QUANTITY_BONUS) {
    return {
      priority: 20,
      exclusive: true,
      combinable: false,
      autoApply: true,
      requiredQuantity: null,
      bonusQuantity: null,
      specialPrice: null,
      bundlePrice: null,
    };
  }

  if (type === PROMOTION_TYPES.BUNDLE) {
    return {
      priority: 30,
      exclusive: true,
      combinable: false,
      autoApply: true,
      requiredQuantity: 1,
      bonusQuantity: 0,
      specialPrice: null,
      bundlePrice: null,
    };
  }

  return {
    priority: 10,
    exclusive: true,
    combinable: false,
    autoApply: true,
    requiredQuantity: 1,
    bonusQuantity: 0,
    specialPrice: null,
    bundlePrice: null,
  };
};

const hasDefinedValue = (value) =>
  value !== undefined && value !== null && value !== "";

const getNumericOrFallback = (value, fallback = null) => {
  if (!hasDefinedValue(value)) {
    return fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const applyPromotionTypeDefaults = (
  promotion = {},
  { overwrite = false } = {},
) => {
  const defaults = getPromotionTypeDefaults(
    promotion.type || PROMOTION_TYPES.SPECIAL_PRICE,
  );

  return overwrite
    ? {
        ...promotion,
        ...defaults,
      }
    : {
        ...defaults,
        ...promotion,
      };
};

export const buildQuantityBonusText = (promotion = {}) => {
  const requiredQuantity = getNumericOrFallback(
    promotion.requiredQuantity,
    null,
  );
  const bonusQuantity = getNumericOrFallback(promotion.bonusQuantity, null);

  if (requiredQuantity > 0 && bonusQuantity > 0) {
    return `si el cliente compra ${requiredQuantity}, se lleva ${bonusQuantity} gratis`;
  }

  return "bonificacion pendiente de configuracion";
};

export const createPromotionItem = (product = {}, overrides = {}) => ({
  productId: product.productId ?? null,
  name: product.name ?? "",
  role: product.role ?? "REQUERIDO",
  quantity: product.quantity ?? 1,
  ...overrides,
});

export const createPromotionDraft = (context = {}) =>
  applyPromotionTypeDefaults({
    id: null,
    code: "",
    name: context.productName
      ? `Promocion ${context.productName}`
      : "Promocion de producto",
    description: "",
    type: PROMOTION_TYPES.SPECIAL_PRICE,
    status: "ACTIVA",
    startAt: getCurrentLocalDateTimeValue(),
    endAt: "",
    fiscalMode: "DESCUENTO_LINEA",
    ...createPromotionTarget(),
    items: [
      createPromotionItem({
        productId: context.productId ?? null,
        name: context.productName ?? "Producto principal",
        role: "REQUERIDO",
        quantity: 1,
      }),
    ],
  });

export const hydratePromotionsFromApi = (promotions = [], context = {}) => {
  if (!Array.isArray(promotions) || promotions.length <= 0) {
    return [];
  }

  return promotions.map((promotion) => ({
    ...createPromotionDraft(context),
    ...applyPromotionTypeDefaults(promotion),
    ...createPromotionTarget(promotion),
    items:
      Array.isArray(promotion.items) && promotion.items.length > 0
        ? promotion.items.map((item) => createPromotionItem(item))
        : [
            createPromotionItem({
              productId: context.productId ?? null,
              name: context.productName ?? "Producto principal",
              role: "REQUERIDO",
              quantity: 1,
            }),
          ],
  }));
};

export const normalizePromotionDrafts = (promotions = [], context = {}) => {
  return (Array.isArray(promotions) ? promotions : [])
    .filter((promotion) => promotion && promotion.name?.trim())
    .map((promotion, index) => {
      const items = Array.isArray(promotion.items) ? promotion.items : [];
      const normalizedItems = items
        .map((item) => ({
          productId:
            item.productId === null || item.productId === undefined
              ? context.productId ?? null
              : Number(item.productId),
          name:
            item.name ||
            (Number(item.productId) === Number(context.productId)
              ? context.productName
              : "Producto relacionado"),
          role: item.role || "REQUERIDO",
          quantity: Number(item.quantity || 1),
        }))
        .filter((item) => item.productId || item.name);

      if (normalizedItems.length <= 0) {
        normalizedItems.push(
          createPromotionItem({
            productId: context.productId ?? null,
            name: context.productName ?? "Producto principal",
            role: "REQUERIDO",
            quantity: Number(promotion.requiredQuantity || 1),
          }),
        );
      }

      return {
        id: promotion.id || null,
        code: promotion.code || null,
        name: promotion.name.trim(),
        description: promotion.description || "",
        type: promotion.type,
        status: promotion.status || "ACTIVA",
        ...applyPromotionTypeDefaults(promotion),
        ...createPromotionTarget(promotion),
        priority: Number(
          promotion.priority ||
            getPromotionTypeDefaults(promotion.type).priority ||
            (index + 1) * 10,
        ),
        startAt: promotion.startAt || getCurrentLocalDateTimeValue(),
        endAt: promotion.endAt || null,
        requiredQuantity: getNumericOrFallback(
          promotion.requiredQuantity,
          getPromotionTypeDefaults(promotion.type).requiredQuantity,
        ),
        bonusQuantity: getNumericOrFallback(
          promotion.bonusQuantity,
          getPromotionTypeDefaults(promotion.type).bonusQuantity,
        ),
        specialPrice:
          promotion.specialPrice === null || promotion.specialPrice === ""
            ? null
            : Number(promotion.specialPrice),
        bundlePrice:
          promotion.bundlePrice === null || promotion.bundlePrice === ""
            ? null
            : Number(promotion.bundlePrice),
        fiscalMode: promotion.fiscalMode || "DESCUENTO_LINEA",
        targetUnitMeasureId:
          promotion.targetUnitMeasureId === undefined ||
          promotion.targetUnitMeasureId === null ||
          promotion.targetUnitMeasureId === ""
            ? null
            : Number(promotion.targetUnitMeasureId),
        targetUnitMeasureName: promotion.targetUnitMeasureName || "",
        targetUnitLabel: promotion.targetUnitLabel || "",
        targetQuantityContained: Number(promotion.targetQuantityContained || 1),
        items: normalizedItems,
      };
    });
};

export const buildPromotionSummary = (promotions = []) => {
  const activePromotions = (Array.isArray(promotions) ? promotions : []).filter(
    (promotion) => promotion?.status !== "INACTIVA",
  );

  if (activePromotions.length <= 0) {
    return {
      count: 0,
      text: "Sin promociones configuradas",
      labels: [],
    };
  }

  const labels = activePromotions.map((promotion) => {
    const targetLabel = promotion.targetUnitLabel || "";
    const targetSuffix = targetLabel ? ` en ${targetLabel.toLowerCase()}` : "";

    if (promotion.type === PROMOTION_TYPES.SPECIAL_PRICE) {
      return `${promotion.name}: precio especial${targetSuffix}`;
    }

    if (promotion.type === PROMOTION_TYPES.QUANTITY_BONUS) {
      return `${promotion.name}: ${buildQuantityBonusText(
        promotion,
      )}${targetSuffix}`;
    }

    return `${promotion.name}: paquete${targetSuffix}`;
  });

  return {
    count: activePromotions.length,
    text:
      activePromotions.length === 1
        ? "1 promocion configurada"
        : `${activePromotions.length} promociones configuradas`,
    labels,
  };
};
